import { useState } from 'react'
import { 
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  DollarSign,
  ShoppingBag,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { Product } from '@/types/database'
import { Link } from 'react-router-dom'

interface ProductCardProps {
  product: Product
  onEdit: (product: Product) => void
  onDelete: (product: Product) => void
}

export function ProductCard({ product, onEdit, onDelete }: ProductCardProps) {
  const [showMenu, setShowMenu] = useState(false)

  // Get the first image from the array or use a default image
  const productImage = product.images?.[0] || "https://images.unsplash.com/photo-1523275335684-37898b6baf30?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1399&q=80"

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="aspect-square relative">
        <img
          src={productImage}
          alt={product.title}
          className="w-full h-full object-cover"
        />
        {product.status === 'pending' && (
          <div className="absolute top-2 left-2 bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium flex items-center">
            <Clock className="h-3 w-3 mr-1" />
            En attente
          </div>
        )}
        {product.status === 'rejected' && (
          <div className="absolute top-2 left-2 bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium flex items-center">
            <XCircle className="h-3 w-3 mr-1" />
            Refusé
          </div>
        )}
        {product.status === 'active' && (
          <div className="absolute top-2 left-2 bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium flex items-center">
            <CheckCircle className="h-3 w-3 mr-1" />
            Actif
          </div>
        )}
        <div className="absolute top-2 right-2">
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              className="bg-white/90 hover:bg-white text-gray-700"
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
            >
              <MoreVertical className="h-5 w-5" />
            </Button>
            {showMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                    onEdit(product);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <Edit className="h-4 w-4 inline mr-2" />
                  Modifier
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                    onDelete(product);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                >
                  <Trash2 className="h-4 w-4 inline mr-2" />
                  Supprimer
                </button>
                <Link 
                  to={`/products/${product.id}`}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => setShowMenu(false)}
                >
                  <Eye className="h-4 w-4 inline mr-2" />
                  Voir
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-4">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 line-clamp-1">
              {product.title}
            </h3>
            <p className="mt-1 text-xs sm:text-sm text-gray-500 line-clamp-2">
              {product.description}
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center text-base sm:text-lg font-bold text-gray-900">
            <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
            {product.price}
          </div>
          {product.min_price && (
            <div className="text-xs sm:text-sm text-gray-500">
              Min: ${product.min_price}
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t grid grid-cols-3 gap-1 sm:gap-2 text-xs sm:text-sm text-gray-500">
          <div className="flex items-center">
            <Eye className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
            <span className="truncate">150 vues</span>
          </div>
          <div className="flex items-center">
            <ShoppingBag className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
            <span className="truncate">5 ventes</span>
          </div>
          <div className="flex items-center">
            <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
            <span className="truncate">2 actifs</span>
          </div>
        </div>
      </div>
    </div>
  )
}